import type { ApiClient } from '@momonos/api-client'
import { formatBdt } from '@momonos/shared'
import { Card, Loader, useToast } from '@momonos/ui'
import { useEffect, useState } from 'react'

/**
 * FR-CIN — the customer intelligence dashboard.
 *
 * Every number here reads rollups; the honest caveat is printed at the top:
 * these metrics get trustworthy as real history accumulates, not before.
 */

interface Payload {
  kpis: {
    totalCustomers: number
    newCustomers: number
    returningCustomers: number
    retentionRate: number
    churnRate: number
    avgVisitFrequency: number
    customerLifetimeValue: number
    avgBasketValue: number
  }
  heatmaps: {
    byHour: Array<{ hour: number; orders: number }>
    byWeekday: Array<{ day: number; orders: number }>
    byMonth: Array<{ month: string; orders: number }>
  }
  segments: Record<string, number>
  sources: Array<{ source: string; redemptions: number; revenue: number }>
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
/** 0–23 as values, so bars key on the hour itself rather than an array index. */
const HOURS = Array.from({ length: 24 }, (_, index) => index)

export function IntelligenceScreen({ api, branchId }: { api: ApiClient; branchId: string | null }) {
  const { show } = useToast()
  const [data, setData] = useState<Payload | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .get<Payload>('/v1/admin/intelligence', { query: { branch: branchId ?? undefined } })
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch(() => {
        if (!cancelled) show('Could not load intelligence', { tone: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [api, branchId, show])

  if (!data) return <Loader label="Crunching the numbers" />

  const percent = (value: number) => `${Math.round(value * 100)}%`
  const maxHour = Math.max(1, ...data.heatmaps.byHour.map((row) => row.orders))
  const maxDay = Math.max(1, ...data.heatmaps.byWeekday.map((row) => row.orders))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-bold text-2xl text-content">Customer intelligence</h1>
        <p className="text-content-muted text-sm">
          Definitions are pinned in code; the numbers earn trust as real order history accumulates.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Tile label="Total customers" value={String(data.kpis.totalCustomers)} />
        <Tile label="New" value={String(data.kpis.newCustomers)} />
        <Tile label="Returning" value={String(data.kpis.returningCustomers)} />
        <Tile label="Retention" value={percent(data.kpis.retentionRate)} />
        <Tile label="Churn" value={percent(data.kpis.churnRate)} />
        <Tile label="Avg visits" value={data.kpis.avgVisitFrequency.toFixed(1)} />
        <Tile label="Lifetime value" value={formatBdt(data.kpis.customerLifetimeValue)} />
        <Tile label="Avg basket" value={formatBdt(data.kpis.avgBasketValue)} />
      </div>

      <Card>
        <h2 className="mb-3 font-semibold text-content">Orders by hour</h2>
        <div className="flex h-28 items-end gap-1">
          {HOURS.map((hour) => {
            const row = data.heatmaps.byHour.find((entry) => entry.hour === hour)
            const height = row ? Math.max(4, (row.orders / maxHour) * 100) : 2
            return (
              <div key={hour} className="flex flex-1 flex-col items-center gap-1">
                <div
                  className="w-full rounded-t bg-momo-red/70"
                  style={{ height: `${height}%` }}
                  title={`${hour}:00 — ${row?.orders ?? 0} orders`}
                />
                {hour % 6 === 0 && <span className="text-[10px] text-content-muted">{hour}</span>}
              </div>
            )
          })}
        </div>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card>
          <h2 className="mb-3 font-semibold text-content">Orders by weekday</h2>
          <div className="space-y-1">
            {data.heatmaps.byWeekday.map((row) => (
              <div key={row.day} className="flex items-center gap-2 text-sm">
                <span className="w-9 text-content-muted">{DAY_NAMES[row.day]}</span>
                <div
                  className="h-4 rounded bg-momo-yellow/70"
                  style={{ width: `${Math.max(2, (row.orders / maxDay) * 100)}%` }}
                />
                <span className="text-content-secondary tabular">{row.orders}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <h2 className="mb-3 font-semibold text-content">Segments</h2>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {Object.entries(data.segments).map(([segment, count]) => (
              <div
                key={segment}
                className="flex justify-between rounded-lg bg-surface-overlay px-3 py-2"
              >
                <span className="text-content-secondary capitalize">
                  {segment.replace('_', ' ')}
                </span>
                <span className="font-semibold text-content tabular">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card>
        <h2 className="mb-3 font-semibold text-content">Revenue by growth source</h2>
        {data.sources.length === 0 ? (
          <p className="text-content-muted text-sm">
            No coupon-attributed orders yet — spinner, referral and campaign revenue lands here.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-3">
            {data.sources.map((row) => (
              <div key={row.source} className="rounded-lg bg-surface-overlay px-4 py-3">
                <p className="text-content-muted text-xs uppercase">{row.source}</p>
                <p className="font-bold text-content text-lg">{formatBdt(row.revenue)}</p>
                <p className="text-content-secondary text-xs">{row.redemptions} orders</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {data.heatmaps.byMonth.length > 0 && (
        <Card>
          <h2 className="mb-3 font-semibold text-content">Orders by month</h2>
          <div className="flex gap-3 overflow-x-auto text-sm">
            {data.heatmaps.byMonth.map((row) => (
              <div key={row.month} className="shrink-0 text-center">
                <p className="font-semibold text-content tabular">{row.orders}</p>
                <p className="text-content-muted">{row.month}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <p className="text-content-muted text-xs uppercase tracking-wide">{label}</p>
      <p className="mt-1 font-bold text-content text-xl tabular">{value}</p>
    </Card>
  )
}
